import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, Clock3, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function Booking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState(null);
  const [avail, setAvail] = useState(null);
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user === false) return navigate("/auth?next=/booking");
    if (!user) return;
    Promise.all([api.get("/orders/my"), api.get("/bookings/availability"), api.get("/bookings/my")])
      .then(([o, a, b]) => {
        const eligible = o.data.filter((x) => x.status !== "awaiting_payment" && x.package_id.includes("interview"));
        setOrders(eligible);
        setAvail(a.data);
        setExisting(b.data);
        if (eligible.length > 0 && !orderId) setOrderId(eligible[0].id);
      })
      .catch(() => setOrders([]));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const book = async () => {
    setBusy(true);
    try {
      await api.post("/bookings", { order_id: orderId, date: selectedDate, time: selectedTime });
      toast.success("Session booked — confirmation in your portal");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  if (user === null || orders === null || avail === null) {
    return <div className="mx-auto max-w-4xl px-5 py-32 text-sm text-slate-500" data-testid="booking-loading">Loading availability…</div>;
  }

  const bookedOrderIds = existing.map((b) => b.order_id);
  const bookable = orders.filter((o) => !bookedOrderIds.includes(o.id));
  const days = avail.days || [];
  const activeDay = days.find((d) => d.date === selectedDate);

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-16 lg:pt-24" data-testid="booking-page">
      <span className="overline-label">Interview Preparation</span>
      <h1 className="mt-4 font-serif text-4xl font-semibold text-slate-50 sm:text-5xl" data-testid="booking-headline">
        <MaskedLine delay={0.1}>Book your 60-minute</MaskedLine>
        <MaskedLine delay={0.3} className="italic text-[#C5A059]">mock interview.</MaskedLine>
      </h1>
      <Reveal delay={0.5}>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400">
          All times are UK time ({avail.timezone}), Monday to Saturday. Your session is a live video call with a UK HR professional — the meeting link is sent to your email before the session.
        </p>
      </Reveal>

      {orders.length === 0 ? (
        <Reveal delay={0.6}>
          <div className="card-surface mt-10 p-10 text-center" data-testid="booking-no-orders">
            <CalendarDays size={32} className="mx-auto text-[#C5A059]" />
            <h2 className="mt-5 font-serif text-2xl font-semibold text-slate-50">No interview session to book</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm text-slate-400">Interview booking unlocks after purchasing 1-on-1 Interview Preparation.</p>
            <button onClick={() => navigate("/order?package=interview-prep")} className="btn-gold mt-8" data-testid="booking-buy-interview-btn">
              Get Interview Preparation <ArrowRight size={15} />
            </button>
          </div>
        </Reveal>
      ) : bookable.length === 0 ? (
        <Reveal delay={0.6}>
          <div className="card-surface mt-10 p-10 text-center" data-testid="booking-already-booked">
            <CheckCircle2 size={32} className="mx-auto text-emerald-400" />
            <h2 className="mt-5 font-serif text-2xl font-semibold text-slate-50">You're all booked</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm text-slate-400">
              Your session is confirmed for {existing[0] && `${new Date(existing[0].date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} at ${existing[0].time} GMT`}. The meeting link will arrive by email.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-ghost mt-8" data-testid="booking-goto-dashboard-btn">Back to Portal</button>
          </div>
        </Reveal>
      ) : (
        <Reveal delay={0.6}>
          <div className="card-surface mt-10 p-8 lg:p-10">
            {bookable.length > 1 && (
              <div className="mb-8">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Session for</label>
                <select value={orderId} onChange={(e) => setOrderId(e.target.value)} className="input-dark" data-testid="booking-order-select">
                  {bookable.map((o) => (
                    <option key={o.id} value={o.id}>{o.package_name} — {new Date(o.created_at).toLocaleDateString("en-GB")}</option>
                  ))}
                </select>
              </div>
            )}

            <h2 className="flex items-center gap-3 font-serif text-xl font-semibold text-slate-50">
              <CalendarDays size={20} className="text-[#C5A059]" /> Pick a day
            </h2>
            <div className="mt-5 grid grid-cols-3 gap-2.5 sm:grid-cols-5 lg:grid-cols-7" data-testid="booking-date-grid">
              {days.map((d) => (
                <button
                  key={d.date}
                  onClick={() => { setSelectedDate(d.date); setSelectedTime(""); }}
                  data-testid={`booking-date-${d.date}`}
                  className={`rounded-xl border px-3 py-3 text-center transition-colors duration-200 ${
                    selectedDate === d.date ? "border-[#C5A059] bg-[#C5A059] text-[#0A0F1D]" : "border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  <span className="block font-mono text-[10px] uppercase tracking-wider opacity-70">{d.label.split(" ")[0]}</span>
                  <span className="block font-serif text-lg font-semibold">{d.label.split(" ").slice(1).join(" ")}</span>
                </button>
              ))}
            </div>

            {selectedDate && (
              <>
                <h2 className="mt-10 flex items-center gap-3 font-serif text-xl font-semibold text-slate-50">
                  <Clock3 size={20} className="text-[#C5A059]" /> Pick a time (GMT)
                </h2>
                <div className="mt-5 grid grid-cols-3 gap-2.5 sm:grid-cols-6" data-testid="booking-time-grid">
                  {activeDay?.times.map((t) => (
                    <button
                      key={t}
                      onClick={() => setSelectedTime(t)}
                      data-testid={`booking-time-${t.replace(":", "")}`}
                      className={`rounded-xl border px-3 py-3 font-mono text-sm transition-colors duration-200 ${
                        selectedTime === t ? "border-[#C5A059] bg-[#C5A059] font-semibold text-[#0A0F1D]" : "border-slate-700 text-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </>
            )}

            <button
              onClick={book}
              disabled={!selectedDate || !selectedTime || busy}
              className="btn-gold mt-10 w-full disabled:opacity-50"
              data-testid="booking-confirm-btn"
            >
              {busy ? "Booking…" : selectedDate && selectedTime
                ? `Confirm ${new Date(selectedDate + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long" })} at ${selectedTime} GMT`
                : "Select a day and time"}
            </button>
          </div>
        </Reveal>
      )}
    </div>
  );
}
